import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue'
import { useTicketsForEventQuery, useCartMutation, useTicketCashPaymentMutation } from '@/features/tickets/api/queries'
import { useUsersInfiniteQuery } from '@/enitites/user/api/queries'
import { useImage } from '@/shared/composables/image'
import { useCart } from '@/features/tickets/composables/useCart'
import { usePricing } from '@/features/tickets/composables/usePricing'
import useValidationRules from '@/shared/composables/validation'
import { ModalWrapper } from '@/shared/ui/modals'
import PurchaseTicketItem from '@/features/tickets/ui/PurchaseTicketItem.vue'
import OrderSummary from '@/features/tickets/ui/OrderSummary.vue'
import BKInput from '@/components/AppShared/BKInput.vue'
import BKButton from '@/components/AppShared/BKButton.vue'
import AvatarPlaceholder from '@/features/tickets/assets/AvatarPlaceholder.vue'
import { UserDB, UsersDBSearchParams } from '@/enitites/user'
import { useEventQuery } from '@/enitites/event/api/queries'
import { mdiMagnify } from '@mdi/js'
import ConfirmationCheck from '@/assets/icons/ConfirmationCheck.vue'
import { IFrontdeskRegistrant } from '@/features/tickets/types'


export default /*#__PURE__*/_defineComponent({
  __name: 'AddAttendeeModal',
  props: {
    eventId: null,
    value: { type: Boolean },
    prefilledUser: null
  },
  emits: ["input", "close-modal", "attendee-added", "manage-ticket"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: boolean): void
  (e: 'close-modal'): void
  (e: 'attendee-added'): void
  (e: 'manage-ticket', ticketId: number): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  eventId: number
  value: boolean
  prefilledUser?: IFrontdeskRegistrant
};





const { getImageById } = useImage()
const rules = useValidationRules()

// Event and tickets data
const { data: event, isLoading: isLoadingEvent, isError: isEventError } = useEventQuery(props.eventId)
const { data: tiers, isLoading: isLoadingTickets, isError: isTicketsError } = useTicketsForEventQuery(props.eventId)
const { mutate: postCart, isPending: isPostingCart, error: errorPostingCart, data: cartSummary } = useCartMutation()
const {
  mutate: postCashPayment,
  isPending: isPostingCashPayment,
  // error: errorPostingCashPayment,
  data: paymentResult,
} = useTicketCashPaymentMutation()

// User search for assignment
const userSearchParams = ref<UsersDBSearchParams>({
  pageNumber: 0,
  pageSize: 50,
  keyword: '',
})
const { data: usersData, isFetching: isLoadingUsers } = useUsersInfiniteQuery(userSearchParams.value)

const STEPS = {
  CHOOSE_TICKETS: 'Choose tickets',
  CHECKOUT: 'Check out',
} as const

const currentStep = ref<keyof typeof STEPS>('CHOOSE_TICKETS')
const tickets = computed(() => tiers.value?.tiers || [])
const users = computed(() => (usersData.value ? usersData.value.pages.flatMap((page) => page) : []))

const { cart, updateCart, isCartEmpty, clearCart, updateContactInfo } = useCart()
const { getTicketById } = usePricing(tickets, cart)

const selectedUser = ref<Partial<UserDB> | string | null>(null)
const fullNameForEmail = ref<string>('')

const isCheckoutDisabled = computed(() => isCartEmpty.value)

// Initialize selectedUser with prefilledUser when modal opens
watch(
  () => props.value,
  (isOpen) => {
    if (isOpen && props.prefilledUser) {
      // Convert IFrontdeskRegistrant to a format compatible with selectedUser
      selectedUser.value = {
        id: props.prefilledUser.userId,
        dancerName: props.prefilledUser.dancerName,
        aEmail: props.prefilledUser.email,
        avatar: props.prefilledUser.avatar,
      }
      fullNameForEmail.value = props.prefilledUser.dancerName
    }
  },
  { immediate: true }
)

watch(
  [selectedUser, fullNameForEmail],
  ([user, fullName]) => {
    let contactEmail = ''
    let contactName = ''

    if (typeof user === 'object' && user && 'aEmail' in user && user.aEmail) {
      contactEmail = user.aEmail
      contactName = user.dancerName || ''
    } else if (typeof user === 'string') {
      contactEmail = user
      contactName = fullName || ''
    }

    updateContactInfo(contactEmail, contactName)
  },
  { immediate: true }
)

const validateUser = (value: unknown) => {
  if (typeof value === 'string') {
    return rules.email(value)
  }
  if (value && typeof value === 'object' && ('id' in value || 'userId' in value)) {
    return true
  }
  return 'Please select a valid user or enter a valid email address.'
}

const closeModal = () => {
  clearCart()
  currentStep.value = 'CHOOSE_TICKETS'
  selectedUser.value = null
  fullNameForEmail.value = ''

  emit('input', false)
  emit('close-modal')
}

const handleManageTicket = () => {
  if (paymentResult.value?.success) {
    emit('manage-ticket', paymentResult.value?.tickets?.[0]?.id)
    closeModal()
  }
}

const handlePurchaseWithCash = () => {
  if (isPostingCart.value || isPostingCashPayment.value || !cartSummary.value || !selectedUser.value) return

  const userId =
    typeof selectedUser.value === 'object' && 'id' in selectedUser.value && selectedUser.value.id
      ? selectedUser.value.id
      : undefined

  postCashPayment({
    eventId: props.eventId,
    data: {
      ...cartSummary.value,
      contactName: cart.value.contactName || 'Not set',
      contactEmail: cart.value.contactEmail || 'Not set',
      userId,
    },
  })
}

watch(
  () => cart.value.tiers,
  (newTiers) => {
    if (newTiers.length > 0) {
      postCart({
        eventId: props.eventId,
        data: {
          tiers: newTiers,
          contactEmail: cart.value.contactEmail,
          contactName: cart.value.contactName,
          discountCode: cart.value.discountCode,
          paymentOption: 'cash',
        },
      })
    }
  },
  { deep: true }
)

const updateUserSelectorKey = ref(0)
const handleSelectedUserChange = () => {
  updateUserSelectorKey.value += 1
}

const getTicketQuantityFromCart = (ticketId: number): number => {
  const tier = cart.value.tiers.find((t) => t.tierId === ticketId)
  return tier ? tier.quantity : 0
}

const getAgeByBirthdate = (birthdate: string): number => {
  const today = new Date()
  const birthDate = new Date(birthdate)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDifference = today.getMonth() - birthDate.getMonth()
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1
  }
  return age
}

const getItemDisplayText = (item: string | Partial<UserDB>): string => {
  if (typeof item === 'string') {
    return item
  }
  if (item.aEmail) {
    return item.aEmail
  }
  return 'Unassigned'
}

const getUserLocationText = (user: Partial<UserDB>): string => {
  let location = user.city || ''
  if (user.state) {
    location += ` ${user.state}`
  }
  if (user.country) {
    location += `, ${user.country}`
  }
  return location
}

return { __sfc: true,props, emit, getImageById, rules, event, isLoadingEvent, isEventError, tiers, isLoadingTickets, isTicketsError, postCart, isPostingCart, errorPostingCart, cartSummary, postCashPayment, isPostingCashPayment, paymentResult, userSearchParams, usersData, isLoadingUsers, STEPS, currentStep, tickets, users, cart, updateCart, isCartEmpty, clearCart, updateContactInfo, getTicketById, selectedUser, fullNameForEmail, isCheckoutDisabled, validateUser, closeModal, handleManageTicket, handlePurchaseWithCash, updateUserSelectorKey, handleSelectedUserChange, getTicketQuantityFromCart, getAgeByBirthdate, getItemDisplayText, getUserLocationText, ModalWrapper, PurchaseTicketItem, OrderSummary, BKInput, BKButton, AvatarPlaceholder, mdiMagnify, ConfirmationCheck }
}

})